import React, { useEffect, useMemo, useState } from "react";
import api, { formatTL } from "@/lib/api";
import { toast } from "sonner";
import {
  Globe, PlayCircle, RefreshCw, Trash2, Eye, Loader2, ArrowRight,
  CheckCircle2, XCircle, Layers, ImageIcon, Tag, DollarSign, Boxes,
  FileText, X, Check, Info, Clock, MoreHorizontal,
} from "lucide-react";

const POLL_MS = 2500;

const STATUS_LABEL = {
  pending: { c: "bg-[#71717A]", t: "Hazırlanıyor" },
  running: { c: "bg-[#C8A96A]", t: "Çalışıyor" },
  done: { c: "bg-[#16A34A]", t: "Tamamlandı" },
  failed: { c: "bg-[#EF4444]", t: "Başarısız" },
  interrupted: { c: "bg-[#F59E0B]", t: "Yarıda Kaldı" },
};

const formatDuration = (sec) => {
  if (sec == null) return "—";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}d ${r}s`;
  const h = Math.floor(m / 60);
  return `${h}sa ${m % 60}d`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

export default function WatchImport() {
  const [jobs, setJobs] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ url: "", brand: "", main_category_id: "", sub_category_name: "" });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [showProducts, setShowProducts] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState({});
  const [syncMode, setSyncMode] = useState(null); // null | "prices" | "full"
  const [syncDiffs, setSyncDiffs] = useState(null);
  const [syncJobId, setSyncJobId] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  const loadJobs = async () => {
    const { data } = await api.get("/admin/imports");
    setJobs(data);
  };
  const loadCats = async () => {
    const { data } = await api.get("/categories");
    setCats(data);
  };

  useEffect(() => { loadJobs(); loadCats(); }, []);

  // Poll active jobs
  useEffect(() => {
    const running = jobs.some((j) => j.status === "pending" || j.status === "running");
    if (!running) return;
    const t = setInterval(loadJobs, POLL_MS);
    return () => clearInterval(t);
  }, [jobs]);

  const onPreview = async () => {
    if (!form.url) return toast.error("URL gerekli");
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/imports/preview", { url: form.url });
      setPreview(data);
      toast.success(`${data.total} ürün bulundu`);
    } catch (e) {
      toast.error("Önizleme başarısız: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPreviewing(false);
    }
  };

  const onStart = async () => {
    if (!form.url || !form.brand) return toast.error("URL ve marka gerekli");
    setStarting(true);
    try {
      const { data } = await api.post("/admin/imports/start", form);
      toast.success("İçe aktarma başlatıldı");
      setPreview(null);
      setForm({ url: "", brand: "", main_category_id: "", sub_category_name: "" });
      await loadJobs();
      const fresh = await api.get(`/admin/imports/${data.job_id}`);
      setActiveJob(fresh.data);
    } catch (e) {
      toast.error("Başlatılamadı: " + (e?.response?.data?.detail || e.message));
    } finally {
      setStarting(false);
    }
  };

  const onRerun = async (job) => {
    if (!window.confirm("Bu kategori URL'sini yeniden tarayıp mevcut ürünleri güncellemek istiyor musunuz?")) return;
    try {
      await api.post(`/admin/imports/${job.id}/rerun`);
      toast.success("Yeniden çalıştırma başlatıldı");
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const onDeleteJob = async (job) => {
    if (!window.confirm("Bu içe aktarma kaydı silinsin mi? Ürünler silinmez.")) return;
    await api.delete(`/admin/imports/${job.id}`);
    loadJobs();
  };

  const openProducts = async (job) => {
    setActiveJob(job);
    setShowProducts(true);
    setSelected({});
    const { data } = await api.get(`/admin/products/by-job/${job.id}`);
    setProducts(data);
  };

  const openDetails = async (job) => {
    setActiveJob(job);
    setShowDetails(true);
    try {
      const { data } = await api.get(`/admin/products/by-job/${job.id}`);
      setProducts(data);
    } catch { setProducts([]); }
  };

  const onSyncPreview = async (job, mode) => {
    setSyncMode(mode);
    setSyncJobId(job.id);
    setSyncDiffs(null);
    setSyncLoading(true);
    try {
      const { data } = await api.get(`/admin/imports/${job.id}/sync-preview?mode=${mode}`);
      setSyncDiffs(data);
    } catch (e) {
      toast.error("Sync önizleme başarısız: " + (e?.response?.data?.detail || e.message));
      setSyncMode(null);
    } finally {
      setSyncLoading(false);
    }
  };

  const applySyncDiffs = async (approvedIdx) => {
    const updates = approvedIdx.map((i) => {
      const d = syncDiffs.diffs[i];
      const fields = {};
      if (d.changes.price) fields.price = d.changes.price.new_supplier_price;
      if (d.changes.description) fields.description = d.changes.description.new;
      if (d.changes.specifications) fields.specifications = d.changes.specifications.new;
      if (d.changes.images) fields.image_urls = d.changes.images.new_urls;
      return { product_id: d.product_id, fields };
    });
    try {
      const { data } = await api.post("/admin/imports/sync-apply", { updates });
      toast.success(`${data.applied} ürün güncellendi`);
      setSyncMode(null); setSyncDiffs(null); setSyncJobId(null);
      loadJobs();
    } catch (e) { toast.error("Güncelleme başarısız"); }
  };

  return (
    <div data-testid="watch-import-page" className="space-y-10">
      <div>
        <h1 className="font-heading text-4xl mb-1">Watch Import Manager</h1>
        <p className="text-sm text-[#71717A]">Tedarikçi kategori URL&apos;sinden ürünleri içeri aktar. Tüm görseller ve veriler yerel olarak saklanır.</p>
      </div>

      {/* Import form */}
      <section className="bg-white border border-border p-6">
        <h2 className="font-heading text-2xl mb-5 flex items-center gap-2"><Globe size={20} /> Yeni İçe Aktarma</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Kategori URL</label>
            <input data-testid="import-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://www.ersasaat.com.tr/pierre-cardin-erkek-saat"
              className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Marka *</label>
            <input data-testid="import-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
              placeholder="Pierre Cardin" className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Ana Kategori</label>
            <select data-testid="import-main-cat" value={form.main_category_id} onChange={(e) => setForm({ ...form, main_category_id: e.target.value })}
              className="w-full border-b border-[#0A0A0A] bg-white py-2 text-sm focus:outline-none focus:border-[#C8A96A]">
              <option value="">(otomatik / Saat kökü)</option>
              {cats.filter((c) => !c.parent_id).map((c) => (
                <option key={c.id} value={c.id}>{c.name_tr}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Alt Kategori (örn. &quot;Erkek Saat&quot;)</label>
            <input data-testid="import-sub-cat" value={form.sub_category_name} onChange={(e) => setForm({ ...form, sub_category_name: e.target.value })}
              placeholder="Erkek Saat" className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
          </div>
        </div>

        <div className="flex gap-3 mt-6 flex-wrap">
          <button data-testid="btn-preview" disabled={previewing || !form.url} onClick={onPreview}
            className="bg-white border border-[#0A0A0A] hover:bg-[#FAF8F3] text-[#0A0A0A] px-6 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50">
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Ürünleri Önizle
          </button>
          <button data-testid="btn-import" disabled={starting || !form.url || !form.brand} onClick={onStart}
            className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50">
            {starting ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} İçe Aktarmayı Başlat
          </button>
        </div>

        {preview && (
          <div className="mt-6 bg-[#FAF8F3] border border-border p-4" data-testid="preview-result">
            <p className="text-xs uppercase tracking-[0.2em] mb-2">Önizleme · {preview.total} ürün bulundu</p>
            <ul className="text-xs space-y-1 max-h-48 overflow-y-auto">
              {preview.product_urls.slice(0, 30).map((u, i) => (
                <li key={i} className="truncate">
                  <a href={u} target="_blank" rel="noreferrer" className="text-[#C8A96A] hover:underline">{u}</a>
                </li>
              ))}
              {preview.total > 30 && <li className="text-[#71717A]">+ {preview.total - 30} daha…</li>}
            </ul>
          </div>
        )}
      </section>

      {/* Import History */}
      <section data-testid="import-history">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-2xl flex items-center gap-2"><Layers size={20} /> İçe Aktarma Geçmişi</h2>
            <p className="text-xs text-[#71717A] mt-1">Toplam {jobs.length} kayıt · birden çok tedarikçi & marka destekli</p>
          </div>
        </div>
        {jobs.length === 0 ? (
          <div className="bg-white border border-border p-10 text-center text-sm text-[#71717A]">Henüz içe aktarma yok</div>
        ) : (
          <div className="bg-white border border-border overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAF8F3] border-b border-border">
                <tr className="text-left">
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px]">Tarih</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px]">Tedarikçi</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px]">Marka</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px]">Kategori URL</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px] text-center">Bulunan</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px] text-center">Eklendi</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px] text-center">Güncellendi</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px] text-center">Başarısız</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px]">Süre</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px]">Durum</th>
                  <th className="p-3 font-medium uppercase tracking-wider text-[10px] text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => <HistoryRow key={j.id} job={j}
                  onDetails={() => openDetails(j)}
                  onOpen={() => openProducts(j)}
                  onRerun={() => onRerun(j)}
                  onDelete={() => onDeleteJob(j)}
                  onSyncPrices={() => onSyncPreview(j, "prices")}
                  onSyncData={() => onSyncPreview(j, "full")} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Details modal */}
      {showDetails && activeJob && (
        <DetailsModal job={activeJob} products={products}
          onClose={() => { setShowDetails(false); loadJobs(); }}
          onOpenProducts={() => { setShowDetails(false); setShowProducts(true); setSelected({}); }} />
      )}

      {/* Products modal */}
      {showProducts && activeJob && (
        <ProductsPanel job={activeJob} products={products} cats={cats} selected={selected} setSelected={setSelected}
          onClose={() => { setShowProducts(false); loadJobs(); }}
          reload={() => openProducts(activeJob)} />
      )}

      {/* Sync preview modal */}
      {syncMode && (
        <SyncPreviewModal mode={syncMode} loading={syncLoading} diffs={syncDiffs}
          onClose={() => { setSyncMode(null); setSyncDiffs(null); }}
          onApply={applySyncDiffs} />
      )}
    </div>
  );
}

function HistoryRow({ job, onDetails, onOpen, onRerun, onDelete, onSyncPrices, onSyncData }) {
  const p = job.progress || {};
  const isLive = job.status === "running" || job.status === "pending";
  const canSync = job.status === "done" || job.status === "interrupted";
  const sb = STATUS_LABEL[job.status] || { c: "bg-[#71717A]", t: job.status };
  // Stall detection: running but no activity in 2 min
  const stalled = isLive && job.last_activity_at && (Date.now() - new Date(job.last_activity_at).getTime()) > 120_000;
  return (
    <tr data-testid={`history-row-${job.id}`} className={`border-b border-border hover:bg-[#FAF8F3] align-top ${stalled ? "bg-[#FFFBEB]" : ""}`}>
      <td className="p-3 whitespace-nowrap">{formatDate(job.created_at)}</td>
      <td className="p-3 whitespace-nowrap font-mono text-[10px] text-[#52525B]">{job.supplier_domain || "—"}</td>
      <td className="p-3 font-medium">{job.brand}{job.sub_category_name ? <span className="text-[#71717A]"> · {job.sub_category_name}</span> : null}</td>
      <td className="p-3 max-w-[220px]"><a href={job.url} target="_blank" rel="noreferrer" className="text-[#C8A96A] hover:underline truncate block" title={job.url}>{job.url}</a></td>
      <td className="p-3 text-center">{p.total ?? 0}</td>
      <td className="p-3 text-center text-[#16A34A] font-medium">{p.imported ?? 0}</td>
      <td className="p-3 text-center text-[#C8A96A] font-medium">{p.updated ?? 0}</td>
      <td className="p-3 text-center text-[#EF4444] font-medium">{p.failed ?? 0}</td>
      <td className="p-3 whitespace-nowrap"><Clock size={10} className="inline mr-1 text-[#71717A]" />{formatDuration(job.duration_seconds)}</td>
      <td className="p-3 whitespace-nowrap">
        <span className={`text-[9px] uppercase tracking-wider text-white px-2 py-1 ${sb.c}`}>{sb.t}</span>
        {stalled && <span className="ml-1 text-[9px] uppercase tracking-wider text-[#92400E] bg-[#FEF3C7] px-2 py-1">Takıldı</span>}
        {isLive && (
          <div className="mt-1.5 w-24 h-1 bg-[#FAF8F3] overflow-hidden">
            <div className="h-full bg-[#C8A96A] transition-all" style={{ width: `${p.percent || 5}%` }} />
          </div>
        )}
        {isLive && <div className="text-[9px] text-[#71717A] mt-0.5">{p.percent ?? 0}%</div>}
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          <ActionIcon onClick={onDetails} testid={`details-${job.id}`} title="Detayları Görüntüle" icon={Info} />
          <ActionIcon onClick={onOpen} disabled={!job.product_ids?.length} testid={`products-${job.id}`} title="Ürünleri Aç" icon={Eye} />
          <ActionIcon onClick={onSyncPrices} disabled={!canSync} testid={`sync-prices-${job.id}`} title="Fiyatları Sync" icon={DollarSign} />
          <ActionIcon onClick={onSyncData} disabled={!canSync} testid={`sync-data-${job.id}`} title="Veriyi Sync" icon={FileText} />
          <ActionIcon onClick={onRerun} disabled={isLive} testid={`rerun-${job.id}`} title="Yeniden Çalıştır" icon={RefreshCw} />
          <ActionIcon onClick={onDelete} testid={`delete-${job.id}`} title="Kaydı Sil" icon={Trash2} danger />
        </div>
      </td>
    </tr>
  );
}

function ActionIcon({ onClick, disabled, testid, title, icon: Icon, danger }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} title={title}
      className={`p-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${danger ? "text-[#EF4444] hover:bg-[#FEF2F2]" : "text-[#52525B] hover:text-[#C8A96A] hover:bg-[#FAF8F3]"}`}>
      <Icon size={14} />
    </button>
  );
}

function DetailsModal({ job, products, onClose, onOpenProducts }) {
  const p = job.progress || {};
  const sb = STATUS_LABEL[job.status] || { c: "bg-[#71717A]", t: job.status };
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get(`/admin/imports/${job.id}/logs`);
      setLogs(data.logs || []);
    } finally { setLogsLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="details-modal" className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-heading text-2xl flex items-center gap-3">
              <span className={`text-[10px] uppercase tracking-wider text-white px-2 py-1 ${sb.c}`}>{sb.t}</span>
              {job.brand}
            </h2>
            <p className="text-xs text-[#71717A] mt-1">{job.supplier_domain || "—"} · İçe Aktarma Detayı</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#FAF8F3]"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Tedarikçi Domain" v={job.supplier_domain || "—"} mono />
            <Field label="Marka" v={job.brand || "—"} />
            <Field label="Alt Kategori" v={job.sub_category_name || "—"} />
            <Field label="Oluşturulma" v={formatDate(job.created_at)} />
            <Field label="Başlangıç" v={formatDate(job.started_at)} />
            <Field label="Bitiş" v={formatDate(job.finished_at)} />
            <Field label="Süre" v={formatDuration(job.duration_seconds)} />
            <Field label="Toplam Bulunan" v={p.total ?? 0} />
            <Field label="Son Aktivite" v={formatDate(job.last_activity_at)} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Kategori URL</div>
            <a href={job.url} target="_blank" rel="noreferrer" className="text-xs text-[#C8A96A] hover:underline break-all">{job.url}</a>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={CheckCircle2} label="Eklendi" v={p.imported ?? 0} color="#16A34A" />
            <Stat icon={RefreshCw} label="Güncellendi" v={p.updated ?? 0} color="#C8A96A" />
            <Stat icon={XCircle} label="Başarısız" v={p.failed ?? 0} color="#EF4444" />
          </div>
          {job.error && (
            <div className="bg-[#FEF2F2] border border-[#FECACA] p-3 text-xs text-[#991B1B]">
              <strong>Hata:</strong> {job.error}
            </div>
          )}
          <div>
            <button data-testid="details-load-logs" onClick={loadLogs} disabled={logsLoading}
              className="border border-[#0A0A0A] hover:bg-[#FAF8F3] text-[10px] uppercase tracking-[0.2em] px-4 py-2 inline-flex items-center gap-1.5">
              {logsLoading ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} İşçi Logları
            </button>
            {logs !== null && (
              <div className="mt-3 bg-[#0A0A0A] text-[#E4E4E7] text-[10px] font-mono p-3 max-h-72 overflow-y-auto whitespace-pre-wrap">
                {logs.length === 0 ? "(boş)" : logs.map((l, i) => (
                  <div key={i} className={l.level === "error" ? "text-[#FCA5A5]" : l.level === "warn" ? "text-[#FCD34D]" : "text-[#A1F0B5]"}>
                    [{(l.ts || "").slice(11, 19)}] {l.level?.toUpperCase()} · {l.msg}
                  </div>
                ))}
              </div>
            )}
          </div>
          {job.errors && job.errors.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-[#EF4444] font-medium">Başarısız Ürünler ({job.errors.length})</summary>
              <ul className="mt-2 space-y-1 max-h-60 overflow-y-auto pl-4 border-l-2 border-[#FEE2E2]">
                {job.errors.map((er, i) => (
                  <li key={i} className="break-all">
                    <a href={er.url} target="_blank" rel="noreferrer" className="text-[#C8A96A] hover:underline">{er.url}</a>
                    <div className="text-[#71717A] text-[10px]">{er.error}</div>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {!!job.product_ids?.length && (
            <div>
              <button data-testid="details-open-products" onClick={onOpenProducts}
                className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white text-xs uppercase tracking-[0.2em] px-5 py-2.5 inline-flex items-center gap-2">
                <Eye size={14} /> {products?.length ?? job.product_ids.length} Ürünü Görüntüle
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono text-[11px]" : ""}`}>{v}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, v, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] flex items-center gap-1.5"><Icon size={12} /> {label}</div>
      <div className="text-lg font-medium mt-0.5" style={{ color: color || "#0A0A0A" }}>{v}</div>
    </div>
  );
}

function ProductsPanel({ job, products, cats, selected, setSelected, onClose, reload }) {
  const [pct, setPct] = useState(10);
  const [bulkCatId, setBulkCatId] = useState("");
  const selIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const allChecked = products.length > 0 && selIds.length === products.length;

  const toggle = (id) => setSelected({ ...selected, [id]: !selected[id] });
  const toggleAll = () => {
    if (allChecked) return setSelected({});
    const next = {}; products.forEach((p) => { next[p.id] = true; });
    setSelected(next);
  };

  const doBulk = async (action, extra = {}) => {
    if (!selIds.length) return toast.error("Hiç ürün seçilmedi");
    if (action === "delete" && !window.confirm(`${selIds.length} ürün silinecek. Emin misin?`)) return;
    try {
      const { data } = await api.post("/admin/products/bulk", { action, product_ids: selIds, ...extra });
      toast.success(`${data.affected} ürün işlendi`);
      setSelected({});
      reload();
    } catch (e) { toast.error("Toplu işlem hatası"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-heading text-2xl">{job.brand} · {products.length} ürün</h2>
            <p className="text-xs text-[#71717A] mt-1">{selIds.length} seçili</p>
          </div>
          <button onClick={onClose} data-testid="close-products" className="p-2 hover:bg-[#FAF8F3]"><X size={20} /></button>
        </div>

        {/* Bulk toolbar */}
        <div className="p-4 bg-[#FAF8F3] border-b border-border flex flex-wrap items-center gap-2 text-xs">
          <button onClick={() => doBulk("publish")} className="bg-[#16A34A] text-white px-3 py-1.5 uppercase tracking-wider">Yayınla</button>
          <button onClick={() => doBulk("unpublish")} className="bg-[#71717A] text-white px-3 py-1.5 uppercase tracking-wider">Taslağa Al</button>
          <button onClick={() => doBulk("delete")} className="bg-[#EF4444] text-white px-3 py-1.5 uppercase tracking-wider">Sil</button>
          <div className="flex items-center gap-1 border border-border bg-white px-2 py-1">
            <input type="number" value={pct} onChange={(e) => setPct(parseFloat(e.target.value) || 0)} className="w-14 text-center focus:outline-none" />
            <span>%</span>
            <button onClick={() => doBulk("price_increase", { percent: pct })} className="ml-1 text-[#16A34A] uppercase tracking-wider">+ Artır</button>
            <button onClick={() => doBulk("price_decrease", { percent: pct })} className="ml-1 text-[#EF4444] uppercase tracking-wider">- Azalt</button>
          </div>
          <div className="flex items-center gap-1 border border-border bg-white px-2 py-1">
            <select value={bulkCatId} onChange={(e) => setBulkCatId(e.target.value)} className="text-xs focus:outline-none">
              <option value="">Kategori seç…</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name_tr}</option>)}
            </select>
            <button onClick={() => bulkCatId && doBulk("category_change", { category_id: bulkCatId })} className="text-[#C8A96A] uppercase tracking-wider">Taşı</button>
          </div>
        </div>

        {/* Products table */}
        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white border-b border-border">
              <tr className="text-left">
                <th className="p-3"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="select-all" /></th>
                <th className="p-3">Görsel</th>
                <th className="p-3">Ad</th>
                <th className="p-3">SKU</th>
                <th className="p-3">Fiyat</th>
                <th className="p-3">Tedarikçi Fiyat</th>
                <th className="p-3">Durum</th>
                <th className="p-3">Görsel #</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-border hover:bg-[#FAF8F3]">
                  <td className="p-3"><input type="checkbox" checked={!!selected[p.id]} onChange={() => toggle(p.id)} data-testid={`select-${p.id}`} /></td>
                  <td className="p-3">
                    {p.images?.[0] ? <img src={p.images[0].startsWith("http") ? p.images[0] : `${process.env.REACT_APP_BACKEND_URL}${p.images[0]}`} alt="" className="w-12 h-12 object-cover" /> : <div className="w-12 h-12 bg-[#FAF8F3] flex items-center justify-center"><ImageIcon size={14} className="text-[#71717A]" /></div>}
                  </td>
                  <td className="p-3 max-w-[280px] truncate">{p.name_tr}</td>
                  <td className="p-3 font-mono text-[10px]">{p.sku}</td>
                  <td className="p-3">{formatTL(p.price)}</td>
                  <td className="p-3 text-[#71717A]">{p.supplier_price ? formatTL(p.supplier_price) : "—"}</td>
                  <td className="p-3">
                    <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 ${p.status === "published" ? "bg-[#16A34A] text-white" : "bg-[#FAF8F3] text-[#71717A]"}`}>{p.status}</span>
                  </td>
                  <td className="p-3">{p.images?.length || 0}</td>
                  <td className="p-3"><a href={`/admin/products`} className="text-[#C8A96A]">Düzenle</a></td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && <div className="p-10 text-center text-sm text-[#71717A]">Bu işin ürünü yok</div>}
        </div>
      </div>
    </div>
  );
}

function SyncPreviewModal({ mode, loading, diffs, onClose, onApply }) {
  const [approved, setApproved] = useState({});

  useEffect(() => {
    if (diffs?.diffs) {
      const init = {}; diffs.diffs.forEach((_, i) => { init[i] = true; });
      setApproved(init);
    }
  }, [diffs]);

  const approvedIdx = Object.keys(approved).filter((k) => approved[k]).map(Number);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="font-heading text-2xl">Sync Önizleme · {mode === "prices" ? "Fiyatlar" : "Tüm Veri"}</h2>
            <p className="text-xs text-[#71717A] mt-1">Onaylanan değişiklikler uygulanır. Onaylanmayanlar atlanır.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#FAF8F3]"><X size={20} /></button>
        </div>
        <div className="overflow-auto flex-1 p-4">
          {loading && <div className="text-center py-16 text-sm text-[#71717A]"><Loader2 className="animate-spin inline mr-2" size={16} /> Tedarikçi sitesi taranıyor…</div>}
          {!loading && diffs?.diffs?.length === 0 && <div className="text-center py-16 text-sm text-[#16A34A]"><Check className="inline mr-1" size={16} /> Değişiklik yok — her şey güncel</div>}
          {!loading && diffs?.diffs?.map((d, i) => (
            <div key={i} className="border border-border p-4 mb-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={!!approved[i]} onChange={() => setApproved({ ...approved, [i]: !approved[i] })} className="mt-1" />
                <div className="flex-1">
                  <p className="font-medium">{d.name}</p>
                  <a href={d.supplier_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#C8A96A] break-all">{d.supplier_url}</a>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
                    {d.changes.price && (
                      <Diff label="Fiyat" old={formatTL(d.changes.price.old)} ne={formatTL(d.changes.price.new_supplier_price)} />
                    )}
                    {d.changes.description && (
                      <Diff label="Açıklama" old={d.changes.description.old + "…"} ne={d.changes.description.new + "…"} />
                    )}
                    {d.changes.specifications && <Diff label="Özellikler" old={`${Object.keys(d.changes.specifications.old).length} alan`} ne={`${Object.keys(d.changes.specifications.new).length} alan`} />}
                    {d.changes.images && <Diff label="Görseller" old={`${d.changes.images.old_count}`} ne={`${d.changes.images.new_count} (yeniden indirilecek)`} />}
                  </div>
                </div>
              </label>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="border border-[#0A0A0A] px-5 py-2 text-xs uppercase tracking-[0.2em]">İptal</button>
          <button onClick={() => onApply(approvedIdx)} disabled={!approvedIdx.length || loading}
            className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-5 py-2 text-xs uppercase tracking-[0.2em] disabled:opacity-50">
            {approvedIdx.length} Değişikliği Uygula
          </button>
        </div>
      </div>
    </div>
  );
}

function Diff({ label, old, ne }) {
  return (
    <div className="bg-[#FAF8F3] p-2">
      <div className="text-[9px] uppercase tracking-wider text-[#71717A]">{label}</div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="line-through text-[#71717A] truncate">{old}</span>
        <ArrowRight size={10} className="shrink-0" />
        <span className="text-[#0A0A0A] truncate">{ne}</span>
      </div>
    </div>
  );
}
